/**
 * @format
 */
import { Component, EventEmitter, Inject, OnDestroy, OnInit } from "@angular/core";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { UploadOutput, UploadInput, UploadFile, humanizeBytes, UploaderOptions, NgUploaderService } from "ngx-uploader";
import { Clipboard } from "@angular/cdk/clipboard";
import { TemplateImagesService } from "../services/template-images.service";
import { Subscription } from "rxjs";
import {
   FieldsAndFlagType,
   ImageFileSimplified,
   SelectedImage,
   UploadImageFile,
   UploadImageInitialState,
   UseImage,
} from "../types";
import { PreviewImageComponent } from "./preview-image.component";
import { PreviewEditTemplateSelectors } from "../../preview-edit-template/state/preview-edit-template.selectors";
import { PreviewEditTemplateActions } from "../../preview-edit-template/state/preview-edit-template.actions";
import { Store } from "@ngrx/store";
import { BrowseUploadedImagesComponent } from "./browse-uploaded-images.component";

@Component({
   selector: "upload-images",
   templateUrl: "upload-images.component.html",
   styleUrls: ["upload-images.component.css"],
})
export class UploadImagesComponent implements OnInit, OnDestroy {
   userFolder = null;
   uploadInUserFolder: boolean;
   options: UploaderOptions;
   initialFile: ImageFileSimplified;
   files: UploadImageFile[];
   uploadInput: EventEmitter<UploadInput>;
   humanizeBytes: Function;
   dragOver: boolean;
   fieldsEditable: Partial<Record<string, Partial<FieldsAndFlagType>>> = {};
   templateModelImageVariable: string;
   displayCopy: boolean;
   browseModalRef: BsModalRef;
   disableUpload = true;
   readonly uploadInUserFolderDefault = true;

   _sub: Subscription = new Subscription();
   _typingTimer: any;
   _inputFocusTimer: any;
   _copyTimer: any;
   _uploaderService: NgUploaderService;

   constructor(
      private modalRef: BsModalRef,
      private modalService: BsModalService,
      private templateImagesService: TemplateImagesService,
      private previewEditTemplateSelectors: PreviewEditTemplateSelectors,
      private store: Store,
      private clipboard: Clipboard,
      @Inject("IMAGE_BASE_URL") private IMAGE_BASE_URL: string,
      @Inject("MAX_FILE_SIZE") private MAX_FILE_SIZE: number
   ) {
      this.options = { concurrency: 1, maxUploads: 3, maxFileSize: this.MAX_FILE_SIZE }; // max size 1MB supported
      this.files = []; // local uploading files array
      this.uploadInput = new EventEmitter<UploadInput>(); // input events, we use this to emit data to ngx-uploader
      this.humanizeBytes = humanizeBytes;
      this.dragOver = false;
      this.uploadInUserFolder = this.uploadInUserFolderDefault;
      this.displayCopy = false;
      this.initialFile = null;
   }

   ngOnInit(): void {
      this._uploaderService = new NgUploaderService(
         this.options.concurrency,
         ["*"],
         this.options.maxUploads,
         this.options.maxFileSize
      );

      this._sub.add(
         this._uploaderService.serviceEvents.subscribe((event: UploadOutput) => {
            this.onUploadOutput(event);
         })
      );
      if (this.uploadInput instanceof EventEmitter) {
         this._sub.add(this._uploaderService.initInputEvents(this.uploadInput));
      }

      this.getUserFolder();
      this.initializeStateFromModalService();
   }

   ngOnDestroy(): void {
      this._sub.unsubscribe();
   }

   /**
    * Close the current dialog and if is necessary remove temporal files
    *
    * @param removeFile the flag to evaluate whether remove temporary files
    */
   closeDialog(removeFile = true): void {
      this.modalRef.hide();
      if (removeFile) {
         this.removeAll();
      }
   }

   onUploadOutput(output: UploadOutput): void {
      switch (output.type) {
         case "addedToQueue":
            if (typeof output.file !== "undefined") {
               (output.file as UploadImageFile).url = this.getUrlImg(output.file.name);
               output.file.id = new Date().getTime().toString() + 'R' + Math.floor(Math.random() * 9999);
               this.files.push(output.file as UploadImageFile);
               setTimeout(() => this.startUpload(output.file), 200);
            }
            break;
         case "uploading":
            if (typeof output.file !== "undefined") {
               // update current data in files array for uploading file
               const index = this.files.findIndex(
                  (file) => typeof output.file !== "undefined" && file.id === output.file.id
               );
               this.files[index] = output.file as UploadImageFile;
            }
            break;
         case "removed":
            // remove file from array when removed
            this.files = this.files.filter((file: UploadFile) => file !== output.file);
            break;
         case "dragOver":
            this.dragOver = true;
            break;
         case "dragOut":
         case "drop":
            this.dragOver = false;
            break;
         case "done":
            this.checkImageNameAndExistence(output.file.name, output.file.id);
            break;
         case "rejected":
            this.templateImagesService.openAlertMaxSizeExceed(output.file.name);
            break;
      }
   }

   startUpload(file: UploadFile): void {
      const event: UploadInput = {
         type: "uploadFile",
         url: "/servlets/templates/upload/",
         method: "POST",
         data: {
            fileName: `${file.id}${this.getFileExtension(file.name)}`,
         },
         file: file,
      };

      this.uploadInput.emit(event);
   }

   useImages(): void {
      if (this.files.length > 0) {
         this.dispatchUseImageUploaded();
      }

      if (this.initialFile && this.files.length === 0) {
         this.updateValueOnTemplateModel(this.initialFile.url);
      }

      this.closeDialog(false);
   }

   removeAll(): void {
      this.files.forEach((file) => this.removeFile(file.id, file.name));
   }

   removeFile(id: string, fileName: string): void {
      this.uploadInput.emit({ type: "remove", id: id });
      const tempFileName = `${id}${this.getFileExtension(fileName)}`;
      this._sub.add(this.templateImagesService.cancelTempUploadedImages(tempFileName).subscribe());
   }

   openPreview(file: UploadFile): void {
      this.modalService.show(PreviewImageComponent, {
         initialState: {
            imageBlob: file.nativeFile,
         },
         backdrop: "static",
         class: "modal-lg modal-not-transform",
         keyboard: false, // disable esc key
      });
   }

   openBrowseUploadedImages(): void {
      this.browseModalRef = this.modalService.show(BrowseUploadedImagesComponent, {
         initialState: {
            baseUrl: this.IMAGE_BASE_URL,
         },
         backdrop: "static",
         class: "modal-md",
         keyboard: false, // disable esc key
      });

      this.browseModalRef.content.onClose.subscribe((result: ImageFileSimplified) => {
         this.initialFile = result;
         if (Boolean(result)) {
            // it enables Use/Replace Image button
            // and is no needed check imageName because it is already uploaded
            this.disableUpload = false;
         }
      });
   }

   copyTextToClipboard(text: string): void {
      this.clipboard.copy(text);
      this.displayCopy = true;
      this._copyTimer = setTimeout(() => {
         this.displayCopy = false;
      }, 1000);
   }

   setFocusForInput(fieldName: string, index: string): void {
      const handleInputFocus = () => {
         const inputElement = document.getElementById(`${index}${fieldName}`);
         inputElement.focus();
      };

      if (this._inputFocusTimer) {
         clearTimeout(this._inputFocusTimer);
      }

      this._inputFocusTimer = setTimeout(handleInputFocus, 50);
   }

   setEditableFlg(fieldName: string, fileName: string, flag: boolean): void {
      if (!this.fieldsEditable[fileName]) {
         this.fieldsEditable = {
            ...this.fieldsEditable,
            [fileName]: {
               [fieldName]: flag,
            },
         };
         return;
      }

      this.fieldsEditable = {
         ...this.fieldsEditable,
         [fileName]: {
            ...this.fieldsEditable[fileName],
            [fieldName]: flag,
         },
      };
      return;
   }

   checkboxUserFolderChanged(checked: boolean): void {
      this.uploadInUserFolder = checked;
      this.updateFilesUrl();
      this.checkAllImageExistence();
   }

   checkFieldAfterTyping(fileId: string, fileName: string): void {
      // console.log('>>> CHECKING EXISTENCE TYPING', {fileName});
      const checkExistence = () => {
         this.checkImageNameAndExistence(fileName, fileId);
      };

      if (this._typingTimer) {
         clearTimeout(this._typingTimer);
      }

      this._typingTimer = setTimeout(checkExistence, 500);
   }

   private setExistenceImage(fileId: string, exist: boolean): void {
      // console.log('>>> SETTING EXISTENCE', {exist});
      const findCondition = (file: UploadImageFile) => file.id === fileId;
      const fileFoundIndex = this.files.findIndex(findCondition);

      if (fileFoundIndex > -1) {
         this.files[fileFoundIndex].isExisting = exist;
         if (exist) {
            this.disableUpload = exist ? exist : this.disableUpload && exist;
         }
      }
   }

   private isImageNameIncorrect(fileId: string, imageNameIncorrect: boolean): void {
      // console.log('>>> SETTING EXISTENCE', {exist});
      const findCondition = (file: UploadImageFile) => file.id === fileId;
      const fileFoundIndex = this.files.findIndex(findCondition);

      if (fileFoundIndex > -1) {
         this.files[fileFoundIndex].imageNameIncorrect = imageNameIncorrect;
         this.disableUpload = imageNameIncorrect ? imageNameIncorrect : this.disableUpload && imageNameIncorrect;
      }
   }

   private getUserFolder(): void {
      this.userFolder = "";
      this.userFolder = `${this.templateImagesService.getSelectedUserIdFromURL()}/`;
   }

   private getUrlImg(fileName: string): string {
      if (this.uploadInUserFolder) {
         return `${this.IMAGE_BASE_URL}${this.userFolder}${fileName}`;
      }
      return `${this.IMAGE_BASE_URL}${fileName}`;
   }

   private updateFilesUrl(): void {
      this.files.forEach((file) => (file.url = this.getUrlImg(file.name)));
   }

   private checkAllImageExistence(): void {
      this.files.forEach((file) => {
         this.checkImageNameAndExistence(this.getFileNameWithFolder(file.name), file.id);
      });
   }

   private checkImageNameAndExistence(filename, fileid): any {
      this._sub.add(
         this.templateImagesService.checkImageNameAndExistence(filename).subscribe((response) => {
            this.setExistenceImage(fileid, response.isFileNameExist);
            this.isImageNameIncorrect(fileid, response.imageNameIncorrect);
         })
      );
   }

   private getFileNameWithFolder(fileName): string {
      if (this.uploadInUserFolder) {
         return `${this.userFolder}${fileName}`;
      }
      return `${fileName}`;
   }

   private getFileExtension(fileName: string): string {
      return fileName.match(/\u002e[a-z]+/g).toString();
   }

   private getImageListToSent(): Array<UseImage> {
      return this.files.map<UseImage>((file) => {
         return {
            fileName: this.uploadInUserFolder ? `${this.userFolder}${file.name}` : `${file.name}`,
            tmpFileName: `${file.id}${this.getFileExtension(file.name)}`,
            variableName: `template.${file.id}`,
            variableValue: file.url,
         };
      });
   }

   private initializeStateFromModalService(): void {
      const state = this.modalService.config.initialState as Partial<UploadImageInitialState>;

      if (state.templateVariable) {
         this.setTemplateImageVariable(state.templateVariable);
      }

      if (state.droppedFiles) {
         // const event: UploadOutput = { type: "drop" };
         // this.uploadOutput.emit(event);
         this._uploaderService.handleFiles(state.droppedFiles);
      }
   }

   private setTemplateImageVariable(templateVariable: string): void {
      this.templateModelImageVariable = templateVariable;
      this._sub.add(
         this.previewEditTemplateSelectors.selectTemplateUpdateModel().subscribe((model) => {
            const value = model.updatedModel[templateVariable];
            if (Boolean(value)) {
               this.initialFile = {
                  url: value,
                  name: value.substring(value.lastIndexOf("/") + 1),
               };
            }
         })
      );
   }

   private dispatchUseImageUploaded(): void {
      const imageList = this.getImageListToSent();
      this.store.dispatch(
         PreviewEditTemplateActions.useImageUploaded({
            imagesList: imageList,
            imageUrl: `${this.IMAGE_BASE_URL}${imageList[0].fileName}`,
            variableName: this.templateModelImageVariable,
            reload: true,
         })
      );
   }

   private updateValueOnTemplateModel(fileUrl: string): void {
      this.store.dispatch(
         PreviewEditTemplateActions.templateUpdateModelVariable({
            variableName: this.templateModelImageVariable,
            value: fileUrl,
            reloadPreview: true,
         })
      );
   }
}
